import { FormEvent, useState } from 'react';
import { useSWRConfig } from 'swr';
import { Area } from 'react-easy-crop';

import { DocumentDto, DocumentTypeCode, EntityTypeCode } from '@/apiClient';

import { usePrivate } from '@/components/private/PrivateContext';
import { getImageByEntityIdCacheKey } from '@/hooks/useGetImageByEntityId';
import { enqueueSnackbar } from '@/providers/SnackbarProvider';

import { messages } from '../messages';
import { SNACKBAR_VARIANT } from '@/constants/notifications';

export type UploadCroppedImageResponse = {
  errorDetails: string | null;
  data: DocumentDto;
  message: string;
  status: number;
  success: boolean;
};

interface Props {
  croppedAreaPixels?: Area;
  documentId?: number;
  entityId?: string;
  entityType: EntityTypeCode;
  documentType: DocumentTypeCode;
  onComplete: () => void;
  onUploaded: (response: UploadCroppedImageResponse) => Promise<void> | void;
}

export function useImageUpload({
  croppedAreaPixels,
  documentId,
  entityId,
  entityType,
  documentType,
  onComplete,
  onUploaded,
}: Props) {
  const { documentApi } = usePrivate();
  const { mutate } = useSWRConfig();
  const [loadingInProgress, setLoadingInProgress] = useState(false);

  const handleSubmit = async (event: FormEvent, image: File) => {
    event.preventDefault();

    if (entityId && croppedAreaPixels && documentApi) {
      setLoadingInProgress(true);
      try {
        // Use context documentApi so the request goes through /api/proxy/core (avoids CORS)
        const response = await documentApi.apiDocumentCroppedPost(
          documentId,
          entityId,
          entityType,
          documentType,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          image,
        );

        if (response.status !== 200) {
          enqueueSnackbar(
            messages['upload-image-modal.submit.unexpected-error'],
            { variant: SNACKBAR_VARIANT.ERROR },
          );
          return;
        }

        // Create response object matching expected format
        const uploadResponse: UploadCroppedImageResponse = {
          errorDetails: null,
          data: response.data,
          message: 'Upload successful',
          status: response.status,
          success: true,
        };

        await mutate(getImageByEntityIdCacheKey(entityId, documentType));
        await onUploaded(uploadResponse);
        onComplete();
      } catch (error) {
        enqueueSnackbar(
          messages['upload-image-modal.submit.unexpected-error'],
          { variant: SNACKBAR_VARIANT.ERROR },
        );
        console.error(
          '[BREEDLOGIC] useUploadImage - Error uploading cropped image',
          error,
        );
      } finally {
        setLoadingInProgress(false);
      }
    }
  };

  return {
    handleSubmit,
    loadingInProgress,
  };
}
